import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, resolveComponent as _resolveComponent, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Transition as _Transition, withCtx as _withCtx, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = {
  class: /*#__PURE__*/_normalizeClass(['registration-form password-form-weblib']),
  "data-qe-id": "form-registration"
}
const _hoisted_2 = ["action"]
const _hoisted_3 = {
  key: 0,
  class: "expandable-input"
}
const _hoisted_4 = {
  key: 0,
  class: "form-message form-error"
}
const _hoisted_5 = /*#__PURE__*/_createElementVNode("i", { class: "icf-warning" }, null, -1)
const _hoisted_6 = { class: "form-group-child" }
const _hoisted_7 = ["value"]

import { computed, onMounted, Ref, ref, watch } from 'vue'
import { ACTION } from '@gd/tracking'
import { marked } from 'marked'
import PasswordInput from '@/components/PasswordInput.vue'
import UserNameInput from '@/components/UserNameInput.vue'
import EmailInput from '@/components/EmailInput.vue'
import { Password } from '@/model/Password'
import { validatePassword } from '@/lib/passwordRequirements'
import { evaluateEmail } from '@/lib/emailRequirements'
import { evaluateName } from '@/lib/userNameRequirements'
import { EMail } from '@/model/EMail'
import { registerUser } from '@/lib/registerUser'
import { handleResponse, ResponseErrorMap } from '@/lib/handleResponse'
import { UserName } from '@/model/UserName'
import { LINK } from '@/model/LINK'
import { ERROR_CODES } from '@/model/ERROR_CODES'
import { useTracking } from '@/lib/trackingComposable'
import { setupComponentI18n } from '@gd/i18n'
import events from '@gd/event-bus'
import { GD, Storage, STORE_NAME } from '@gd/storage'

interface Props {
  appName?: GD_playerProfile.AppName
  nativeAppVersion?: string
  platform?: GD_playerProfile.Platform
  formId?: string
  originalQuery?: string
  landingPageId?: string
  stepped?: boolean | string
  colorScheme?: string
  actionURL?: string
}

const STATE_EXPANDED = 'isExpanded'


export default /*#__PURE__*/_defineComponent({
  __name: 'RegistrationForm',
  props: {
    appName: { default: 'www' },
    nativeAppVersion: { default: '' },
    platform: { default: 'desktop' },
    formId: {},
    originalQuery: { default: '' },
    landingPageId: { default: '' },
    stepped: { type: [Boolean, String], default: false },
    colorScheme: { default: '' },
    actionURL: { default: '' }
  },
  emits: ['success', 'link', 'step'],
  setup(__props: any, { emit: __emit }) {

setupComponentI18n()
const { track } = useTracking()

const props = __props

const emit = __emit

const loginUrl = ref('') // 'https://mein.gameduell.de/gd/login/firstLogin.xhtml'
const colorScheme = ref('')
const errorCode: Ref<'main' | ''> = ref('')

const stateClass: Ref<string | null> = ref(null)

const form = ref(null)
const passwordInput: Ref<typeof PasswordInput | null> = ref(null)

const name: Ref<UserName> = ref({
  value: '',
  valid: false,
  errorCode: undefined,
  length: false,
  namePattern: false,
  isAvailable: false,
})

const mail: Ref<EMail> = ref({
  value: '',
  valid: false,
  errorCode: undefined,
  pattern: false,
  length: false,
})

const password: Ref<Password> = ref({
  value: '',
  valid: false,
  errorCode: undefined,
  length: false,
  numbers: false,
  specialChars: false,
  lowerUpperCase: false,
  scoreOk: false,
  sameAsOldPassword: false,
  sameAsUserName: false,
  similarToUserName: false,
  sameAsEmail: false,
  score: 0,
})

// used to activate the error text below the input field ( different behavior for (name, mail) and password)
const enableMailValidation = ref(false)
const enableNameValidation = ref(false)
const enablePasswordValidation = ref(false)

const buttonProgress = ref(false)

const hasSteps = computed(() => {
  return props.stepped === '' || props.stepped === 'true' || props.stepped === true
})

const currentStep = computed(() => {
  return stateClass.value !== STATE_EXPANDED ? 1 : 2
})

const formName = computed(() => {
  let fId = props.formId

  if (fId && hasSteps) {
    fId += `_${currentStep.value}`
  }

  return fId
})

const userTimeZone = computed(() => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
})

watch(
  () => stateClass.value,
  (to: string) => {
    emit('step', to !== STATE_EXPANDED ? 1 : 2)
  },
)

onMounted(() => {
  stateClass.value = hasSteps.value ? null : STATE_EXPANDED
  loginUrl.value = props.actionURL
  colorScheme.value = props.colorScheme
  errorCode.value = ''
})

const _evaluateName = (validateNameInUse = true) => {
  enableNameValidation.value = true
  return new Promise((res, rej) => {
    if (validateNameInUse) {
      evaluateName(name.value).then((n) => {
        name.value = n
        if (name.value.valid && name.value.isAvailable) {
          res(true)
        } else {
          rej({ name: name.value.errorCode })
        }
      }) // currently there is not catch case
    } else {
      if (name.value.value.length >= 3) {
        res(true)
      } else {
        name.value.errorCode = ERROR_CODES.nameToShort
        rej({ name: name.value.errorCode })
      }
    }
  })
}
/**
 * call this for the final password evaluation
 */
const _evaluatePassword = () => {
  // correct?
  enablePasswordValidation.value = true
  return new Promise((res, err) => {
    password.value = validatePassword({
      password: password.value.value,
      name: name.value.value,
      email: mail.value.value,
    })
    if (password.value.valid) res(true)
    else err({ password: password.value.errorCode })
  })
}

const _evaluateMail = () => {
  enableMailValidation.value = true
  return new Promise((res, err) => {
    evaluateEmail(mail.value).then((m) => {
      mail.value = m
      if (mail.value.valid) {
        res(true)
      } else {
        err({ mail: mail.value.errorCode })
      }
    })
  })
}

const action = () => {
  track({
    id: formName.value || 'registration-form',
    action: ACTION.click,
    elem: 'registration_button',
    key: props.appName,
    context: props.appName,
    app_platform: props.platform,
    ...(props.landingPageId && { landing_page_id: props.landingPageId }),
  })
  if (stateClass.value !== STATE_EXPANDED) {
    _evaluateName(false)
      .then((succeeded) => {
        if (succeeded === true) {
          stateClass.value = STATE_EXPANDED
          setTimeout(() => {
            passwordInput.value?.focus()
          }, 300)
          return _evaluateName()
        }
      })
      .catch((err) => {
        track({
          key: formName.value || 'registration-form',
          id: 'error-submit',
          elem: 'registration_button',
          data: err.name,
          context: props.appName,
          app_platform: props.platform,
          ...(props.landingPageId && { landing_page_id: props.landingPageId }),
        })
      })
  } else {
    buttonProgress.value = true
    name.value.value = name.value.value.trim()
    mail.value.value = mail.value.value.trim()
    Promise.allSettled([_evaluatePassword(), _evaluateName(), _evaluateMail()])
      .then((results: PromiseSettledResult<GD_userRegistration.RegisterResponse>[] | PromiseRejectedResult[]) => {
        const succeeded = results.filter((result) => result.status === 'rejected').length === 0

        if (!succeeded) {
          let err = {}
          results.forEach((result) => {
            err = {
              ...err,
              ...(result as PromiseRejectedResult).reason,
            }
          })
          throw err
        }
        const data: GD_userRegistration.RegistrationData = {
          commonName: name.value.value,
          password: password.value.value,
          email: mail.value.value,
          // TODO get this as attribute
          originalQuery: props.originalQuery,
          landingPageId: props.landingPageId,
          newRegistrationForm: true,
          platform: props.platform,
          tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }
        if (props.nativeAppVersion) {
          data.nativeAppVersion = props.nativeAppVersion
        }
        // all inputs are valid
        // register the user before sending the form
        return registerUser(data)
      })
      .then((response) => {
        // registration successfully - go for login
        return handleResponse(response)
      })
      .then((success: GD_userRegistration.RegisterResponse) => {
        // submit the form for the login :)
        if (process.env.NODE_ENV === 'production') {
          if (loginUrl.value) {
            form.value.submit()
          } else {
            emit('success', {
              name: name.value.value,
              pwHash: success.pwHash,
            })
          }
        } else {
          buttonProgress.value = false
        }
      })
      .catch((err: ResponseErrorMap) => {
        buttonProgress.value = false
        if (err.major) {
          errorCode.value = 'main'
        }
        if (err.name) {
          name.value.errorCode = err.name
        }
        if (err.password) {
          password.value.errorCode = err.password
        }
        if (err.mail) {
          mail.value.errorCode = err.mail
        }
        // send tracking data
        track({
          key: formName.value || 'registration-form',
          id: 'error-submit',
          elem: 'registration_button',
          data: Object.keys(err)
            .filter((key) => !!(err as any)[key])
            .map((key) => (err as any)[key])
            .join(','),
          context: props.appName,
          app_platform: props.platform,
          ...(props.landingPageId && { landing_page_id: props.landingPageId }),
        })
      })
  }
  return false
}

const preventSubmit = (event: Event) => {
  event.preventDefault()
  event.stopPropagation()
}

const onMdLink = (event: Event) => {
  const target = event.target as HTMLAnchorElement
  if (target?.tagName === 'A') {
    if (target.href && /#privacy/.test(target.href)) {
      if (process.env.VUE_APP_BUILD_TARGET === 'common') {
        emit('link', LINK.PRIVACY)
      } else {
        location.href = '/gd/help/privacy.xhtml'
      }
    }
  }
  event.preventDefault()
}

const md = (s: string) => {
  return marked(s)
}

const onHelpshift = () => {
  track({
    key: formName.value || 'registration-form',
    id: 'password-hint',
    action: ACTION.click,
    elem: 'link_to_passwordhelp',
    context: props.appName,
    app_platform: props.platform,
    ...(props.landingPageId && { landing_page_id: props.landingPageId }),
  })
  // desktop we call default
  if (props.platform === 'desktop') {
    const storageGD: GD = new Storage().read(STORE_NAME.GD)
    events.$emit('helpshift-article', { name: 'passwordhelp', lang: storageGD.viewLang, disableLinks: false })
  } else {
    // all others (mobile-web, ...) shoul get own controll
    emit('link', LINK.PASSWORD_HELP)
  }
}

return (_ctx: any,_cache: any) => {
  const _component_t_text = _resolveComponent("t-text")!
  const _component_t_button = _resolveComponent("t-button")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("form", {
      ref_key: "form",
      ref: form,
      onSubmit: _withModifiers(action, ["prevent"]),
      class: "form-gouping",
      action: loginUrl.value
    }, [
      _createVNode(UserNameInput, {
        class: "input-username",
        modelValue: name.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((name).value = $event)),
        validate: enableNameValidation.value,
        onReset: _cache[1] || (_cache[1] = ($event: any) => (enableNameValidation.value = false))
      }, null, 8, ["modelValue", "validate"]),
      _createVNode(_Transition, { name: "expandable" }, {
        default: _withCtx(() => [
          (hasSteps.value === false || (hasSteps.value && stateClass.value === 'isExpanded'))
            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                _createVNode(PasswordInput, {
                  ref_key: "passwordInput",
                  ref: passwordInput,
                  class: "input-password",
                  modelValue: password.value,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((password).value = $event)),
                  name: name.value.value,
                  email: mail.value.value,
                  validate: enablePasswordValidation.value,
                  onReset: _cache[3] || (_cache[3] = ($event: any) => (enablePasswordValidation.value = false)),
                  onHelpshift: onHelpshift
                }, null, 8, ["modelValue", "name", "email", "validate"]),
                _createVNode(EmailInput, {
                  class: "input-email",
                  modelValue: mail.value,
                  "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((mail).value = $event)),
                  validate: enableMailValidation.value,
                  onReset: _cache[5] || (_cache[5] = ($event: any) => (enableMailValidation.value = false))
                }, null, 8, ["modelValue", "validate"]),
                _createVNode(_component_t_text, {
                  onClick: onMdLink,
                  innerHTML: md(_ctx.$t('pfwRegistration.main.policy_md')),
                  "data-qe-id": "policy-text",
                  class: "policy-text"
                }, null, 8, ["innerHTML"]),
                _createVNode(_Transition, { name: "fade" }, {
                  default: _withCtx(() => [
                    (errorCode.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                          _hoisted_5,
                          _createElementVNode("span", null, _toDisplayString(_ctx.$t(`pfwRegistration.error.${errorCode.value}`)), 1)
                        ]))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                })
              ]))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }),
      _createElementVNode("div", _hoisted_6, [
        _createElementVNode("input", {
          type: "hidden",
          id: "tz",
          name: "tz",
          value: userTimeZone.value
        }, null, 8, _hoisted_7),
        _createElementVNode("div", {
          class: "t-button-group",
          onClick: _cache[6] || (_cache[6] = ($event: any) => (preventSubmit($event)))
        }, [
          _createVNode(_component_t_button, {
            onClick: action,
            progress: buttonProgress.value,
            color: colorScheme.value,
            complementary: ""
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(stateClass.value === STATE_EXPANDED ? _ctx.$t('pfwRegistration.main.button') : _ctx.$t('pfwRegistration.main.button_next')) + " ", 1),
              (stateClass.value !== STATE_EXPANDED)
                ? (_openBlock(), _createElementBlock("span", {
                    key: 0,
                    class: _normalizeClass({ 'icf-arrowRight': stateClass.value !== 'isExpanded' })
                  }, null, 2))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }, 8, ["progress", "color"])
        ])
      ])
    ], 40, _hoisted_2)
  ]))
}
}

})